import { G } from '@mobily/ts-belt';
import { addHours, format } from 'date-fns';
import { FieldValues } from 'react-hook-form';
import { match, P } from 'ts-pattern';

import { B2B_CAMPAIGN_GRACE_PERIOD_HOURS } from '@Common/constants/b2b/campaigns';
import { GetB2bCampaignsIdEndpoint } from '@Common/types/bo/b2b/campaigns';

import {
  ConfirmationDialogOpen,
  shouldConfirmCompanyCollectedChoices,
  shouldConfirmConsultationPeriodLength,
  shouldConfirmRemovedEmployees,
} from '@sh4res/bo-common/components/B2bCampaigns/confirmation_dialog';
import { isCampaignVacationDaysAllocation } from '@sh4res/bo-common/components/B2bCampaigns/helpers/isCampaignVacationDaysAllocation';
import { AffiliatesDataStep } from '@sh4res/bo-common/components/B2bCampaigns/steps/affiliates_data/AffiliatesDataStep';
import {
  AllocationRecord,
  AllocationsConfirmationStep,
  isAllocationAccepted,
} from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/AllocationsConfirmationStep';
import { AllocationsStep } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/AllocationsStep';
import { VacationDaysAllocationsConfirmationStep } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/VacationDaysAllocationsConfirmationStep/VacationDaysAllocationsConfirmationStep';
import { VacationDaysAllocationsStep } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/VacationDaysAllocationsStep/VacationDaysAllocationsStep';
import { VacationDaysAllocationFormRow } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/vacationDaysAllocationRows';
import { hasAnyVacationDays } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/vacationDaysFile';
import { ConfirmationStep } from '@sh4res/bo-common/components/B2bCampaigns/steps/confirmation/ConfirmationStep';
import { GeneralDetailsStep } from '@sh4res/bo-common/components/B2bCampaigns/steps/GeneralDetailsStep/GeneralDetailsStep';
import { PaymentStep } from '@sh4res/bo-common/components/B2bCampaigns/steps/PaymentStep';
import {
  validateCampaignAllocations,
  validateCampaignConfirmationStep,
  validateCampaignGeneralInfoStep,
  validateVacationDaysAllocations,
} from '@sh4res/bo-common/components/B2bCampaigns/validators';
import { StepperFormStep } from '@sh4res/bo-common/components/StepperForm';

const getUnusedVacationDaysStepLocked = (
  data: FieldValues,
  step:
    | 'general'
    | 'affiliates'
    | 'allocations'
    | 'vacation_days_allocations'
    | 'vacation_days_allocations_review'
    | 'allocation_review'
    | 'payment'
    | 'confirmation',
  campaignStatus?: GetB2bCampaignsIdEndpoint['Client']['ResponseBody']['status'],
): boolean => {
  if (!isCampaignVacationDaysAllocation(data)) {
    return false;
  }
  if (step === 'vacation_days_allocations' || step === 'vacation_days_allocations_review') {
    return campaignStatus !== 'campaign_ending_grace_period';
  }
  return (!campaignStatus || campaignStatus === 'campaign_draft') && step !== 'general';
};

const isDistributorFlowDraftStatus = (
  campaignStatus?: GetB2bCampaignsIdEndpoint['Client']['ResponseBody']['status'],
): boolean =>
  G.isNullable(campaignStatus) ||
  campaignStatus === 'campaign_draft' ||
  campaignStatus === 'campaign_pending_allocation_computation' ||
  campaignStatus === 'campaign_awaiting_customer_confirmation';

const getAllocationComputationIsLocked = ({
  allocationComputedBy,
  choiceCollectedBy,
  step,
  isBoExternal,
  campaignStatus,
  distributorStepsOverride,
}: {
  allocationComputedBy: GetB2bCampaignsIdEndpoint['Client']['ResponseBody']['allocation_computed_by'];
  choiceCollectedBy: GetB2bCampaignsIdEndpoint['Client']['ResponseBody']['choice_collected_by'];
  campaignStatus?: GetB2bCampaignsIdEndpoint['Client']['ResponseBody']['status'];
  step: 'general' | 'affiliates' | 'allocations' | 'allocation_review' | 'payment' | 'confirmation';
  isBoExternal: boolean;
  distributorStepsOverride: boolean;
}): boolean => {
  if (allocationComputedBy !== 'distributor') {
    // Lock affiliates step on all other options
    return step === 'affiliates';
  }

  // One stage unlocks per save, so the status still walks to_compute -> computed -> confirmed.
  if (distributorStepsOverride && !isBoExternal && isDistributorFlowDraftStatus(campaignStatus)) {
    return match(step)
      .with(P.union('general', 'affiliates'), () => false)
      .with(
        P.union('allocations', 'allocation_review'),
        () => G.isNullable(campaignStatus) || campaignStatus === 'campaign_draft',
      )
      .with(
        P.union('payment', 'confirmation'),
        () => campaignStatus !== 'campaign_awaiting_customer_confirmation',
      )
      .exhaustive();
  }

  return match(step)
    .with('general', () => false)
    .with('affiliates', () => {
      if (isBoExternal) {
        return false;
      }
      return campaignStatus !== 'campaign_pending_allocation_computation';
    })
    .with('allocations', () => {
      if (isBoExternal) {
        // In option 3 the client only sets the consultation period here
        return (
          choiceCollectedBy !== 'distributor' ||
          (G.isNotNullable(campaignStatus) && campaignStatus !== 'campaign_draft')
        );
      }
      return campaignStatus !== 'campaign_pending_allocation_computation';
    })
    .with('allocation_review', () => {
      if (isBoExternal) {
        return campaignStatus !== 'campaign_awaiting_customer_confirmation';
      }
      return campaignStatus !== 'campaign_pending_allocation_computation';
    })
    .with(P.union('payment', 'confirmation'), () => {
      if (isBoExternal) {
        return campaignStatus !== 'campaign_awaiting_customer_confirmation';
      }
      return true;
    })
    .exhaustive();
};

const getHandoverToDistributorSave = (
  openConfirmationDialog: ConfirmationDialogOpen,
): NonNullable<StepperFormStep['customSave']> => ({
  transform: (data: FieldValues) => ({
    ...data,
    is_draft: true,
    allocation_computation_status: 'to_compute',
  }),
  label: 'ra.action.save',
  onSuccess: (id) => {
    openConfirmationDialog({
      campaignId: id as number,
      title:
        'resources.bo_common.b2b.campaigns.sections.success_modal_allocations_to_compute.title',
      body: 'resources.bo_common.b2b.campaigns.sections.success_modal_allocations_to_compute.body',
      showAllocations: false,
    });
  },
});

export const getSteps = ({
  isBoExternal,
  campaignStatus,
  action,
  openConfirmationDialog,
  isOngoingConsultationEdit = false,
  distributorStepsOverride = false,
  isFundRequestPaymentMethodEdit = false,
  isCetConsultationPeriodEdit = false,
}: {
  isBoExternal: boolean;
  campaignStatus?: GetB2bCampaignsIdEndpoint['Client']['ResponseBody']['status'];
  action: 'edit' | 'create';
  openConfirmationDialog: ConfirmationDialogOpen;
  isOngoingConsultationEdit?: boolean;
  distributorStepsOverride?: boolean;
  isFundRequestPaymentMethodEdit?: boolean;
  // the general step, holding the CET consultation period, becomes the only step and saves directly
  isCetConsultationPeriodEdit?: boolean;
}): StepperFormStep[] => [
  {
    label: 'resources.bo_common.b2b.campaigns.sections.general.title',
    content: (
      <GeneralDetailsStep
        action={action}
        isOngoingConsultationEdit={isOngoingConsultationEdit}
        isCetConsultationPeriodEdit={isCetConsultationPeriodEdit}
      />
    ),
    locked: isFundRequestPaymentMethodEdit,
    validate: validateCampaignGeneralInfoStep,
    hideDraft: (data: FieldValues) => isCampaignVacationDaysAllocation(data),
    nextDisabled: (data: FieldValues) =>
      data.matching_contribution_config_missing_for_year === true ||
      data.pass_missing_for_reference_year === true ||
      data.contracts_agreement_distributions_mismatch === true,
    draftDisabled: (data: FieldValues) =>
      G.isNullable(data.company_id) ||
      !data.b2b_contract_ids?.length ||
      G.isNullable(data.type) ||
      G.isNullable(data.fiscal_period_start) ||
      G.isNullable(data.fiscal_period_end) ||
      data.matching_contribution_config_missing_for_year === true ||
      data.pass_missing_for_reference_year === true ||
      data.contracts_agreement_distributions_mismatch === true,
    // Custom save only available for option 3
    customSave: {
      transform: (data: FieldValues) => ({
        ...data,
        // cant be draft for CET option 2
        is_draft: !data.allocations?.some((allocation: VacationDaysAllocationFormRow) =>
          hasAnyVacationDays(allocation),
        ),
      }),
      label: 'ra.action.save',
      onSuccess: (id, data) => {
        if (
          !data.allocations?.some((allocation: VacationDaysAllocationFormRow) =>
            hasAnyVacationDays(allocation),
          ) ||
          !data.end_date
        ) {
          return;
        }
        const consolidationDate = format(
          addHours(new Date(data.end_date as string), B2B_CAMPAIGN_GRACE_PERIOD_HOURS),
          'dd/MM/yyyy',
        );
        openConfirmationDialog({
          campaignId: id as number,
          title:
            action === 'edit'
              ? 'resources.bo_common.b2b.campaigns.sections.success_modal_updated.title'
              : 'resources.bo_common.b2b.campaigns.sections.success_modal.title',
          body: 'resources.bo_common.b2b.campaigns.sections.success_modal_unused_vacation_days.body',
          bodyArgs: { date: consolidationDate },
          showAllocations: false,
        });
      },
    },
    ...(isCetConsultationPeriodEdit && { customSave: undefined }),
  },
  {
    label: 'resources.bo_common.b2b.campaigns.sections.affiliates.title',
    content: (
      <AffiliatesDataStep
        isBoExternal={isBoExternal}
        distributorStepsOverride={distributorStepsOverride}
      />
    ),
    locked: (data: FieldValues) =>
      isFundRequestPaymentMethodEdit ||
      getAllocationComputationIsLocked({
        isBoExternal,
        allocationComputedBy: data.allocation_computed_by,
        choiceCollectedBy: data.choice_collected_by,
        campaignStatus,
        step: 'affiliates',
        distributorStepsOverride,
      }) ||
      (data.has_er_plan as boolean),
    hidden: (data: FieldValues) =>
      data.allocation_computed_by !== 'distributor' || isCampaignVacationDaysAllocation(data),
    // Custom save only available for option 3
    customSave: getHandoverToDistributorSave(openConfirmationDialog),
  },
  {
    label: 'resources.bo_common.b2b.campaigns.sections.allocations.title',
    content: (
      <AllocationsStep
        isBoExternal={isBoExternal}
        isOngoingConsultationEdit={isOngoingConsultationEdit}
        distributorStepsOverride={distributorStepsOverride}
      />
    ),
    confirmation: shouldConfirmConsultationPeriodLength,
    hidden: (data: FieldValues) => isCampaignVacationDaysAllocation(data),
    locked: (data: FieldValues) =>
      isFundRequestPaymentMethodEdit ||
      (!isOngoingConsultationEdit &&
        (getAllocationComputationIsLocked({
          isBoExternal,
          allocationComputedBy: data.allocation_computed_by,
          choiceCollectedBy: data.choice_collected_by,
          campaignStatus,
          step: 'allocations',
          distributorStepsOverride,
        }) ||
          (data.has_er_plan as boolean) ||
          getUnusedVacationDaysStepLocked(data, 'allocations', campaignStatus))),
    // In option 3 this is the last step the client can reach, so the handover happens here
    customSave: getHandoverToDistributorSave(openConfirmationDialog),
  },
  {
    label: 'resources.bo_common.b2b.campaigns.sections.allocations.title',
    content: <VacationDaysAllocationsStep />,
    hidden: (data: FieldValues) => !isCampaignVacationDaysAllocation(data),
    locked: (data: FieldValues) =>
      isFundRequestPaymentMethodEdit ||
      (data.has_er_plan as boolean) ||
      getUnusedVacationDaysStepLocked(data, 'vacation_days_allocations', campaignStatus),
    hideDraft: true,
  },
  {
    label: 'resources.bo_common.b2b.campaigns.sections.review.title',
    content: <VacationDaysAllocationsConfirmationStep />,
    validate: validateVacationDaysAllocations,
    locked: (data: FieldValues) =>
      isFundRequestPaymentMethodEdit ||
      (data.has_er_plan as boolean) ||
      getUnusedVacationDaysStepLocked(data, 'vacation_days_allocations_review', campaignStatus),
    hideDraft: true,
    hidden: (data: FieldValues) => !isCampaignVacationDaysAllocation(data),
  },
  {
    label: 'resources.bo_common.b2b.campaigns.sections.review.title',
    content: (
      <AllocationsConfirmationStep
        action={action}
        isBoExternal={isBoExternal}
        isOngoingConsultationEdit={isOngoingConsultationEdit}
        distributorStepsOverride={distributorStepsOverride}
      />
    ),
    validate: validateCampaignAllocations,
    locked: (data: FieldValues) =>
      isFundRequestPaymentMethodEdit ||
      (!isOngoingConsultationEdit &&
        (getAllocationComputationIsLocked({
          isBoExternal,
          allocationComputedBy: data.allocation_computed_by,
          choiceCollectedBy: data.choice_collected_by,
          campaignStatus,
          step: 'allocation_review',
          distributorStepsOverride,
        }) ||
          (data.has_er_plan as boolean) ||
          getUnusedVacationDaysStepLocked(data, 'allocation_review', campaignStatus))),
    hidden: (data: FieldValues) => isCampaignVacationDaysAllocation(data),
    // Custom save only available for option 3
    customSave: {
      transform: (data: FieldValues) => ({
        ...data,
        is_draft: true,
        allocation_computation_status: 'computed',
        allocations: (data.allocations ?? []).filter(
          (entry: AllocationRecord) => (entry.amount ?? 0) > 0,
        ),
      }),
      label: 'ra.action.save',
      onSuccess: (id) => {
        openConfirmationDialog({
          campaignId: id as number,
          title:
            'resources.bo_common.b2b.campaigns.sections.success_modal_allocations_computed.title',
          body: 'resources.bo_common.b2b.campaigns.sections.success_modal_allocations_computed.body',
          showAllocations: true,
        });
      },
    },
    confirmation: shouldConfirmCompanyCollectedChoices,
  },
  {
    label: 'resources.bo_common.b2b.campaigns.sections.payment.title',
    content: (
      <PaymentStep
        isBoExternal={isBoExternal}
        isOngoingConsultationEdit={isOngoingConsultationEdit}
        distributorStepsOverride={distributorStepsOverride}
        isFundRequestPaymentMethodEdit={isFundRequestPaymentMethodEdit}
      />
    ),
    locked: (data: FieldValues) =>
      isCetConsultationPeriodEdit ||
      (!isFundRequestPaymentMethodEdit &&
        !isOngoingConsultationEdit &&
        (getAllocationComputationIsLocked({
          isBoExternal,
          allocationComputedBy: data.allocation_computed_by,
          choiceCollectedBy: data.choice_collected_by,
          campaignStatus,
          step: 'payment',
          distributorStepsOverride,
        }) ||
          (data.has_er_plan as boolean) ||
          getUnusedVacationDaysStepLocked(data, 'payment', campaignStatus))),
    hideDraft: (data: FieldValues) => isCampaignVacationDaysAllocation(data),
  },
  {
    label: 'resources.bo_common.b2b.campaigns.sections.confirmation.title',
    content: <ConfirmationStep />,
    // Only the active step's validator runs on save, and an ongoing edit saves from here.
    validate: validateCampaignConfirmationStep(isOngoingConsultationEdit),
    confirmation: isOngoingConsultationEdit ? shouldConfirmRemovedEmployees : undefined,
    locked: (data: FieldValues) =>
      isFundRequestPaymentMethodEdit ||
      isCetConsultationPeriodEdit ||
      (!isOngoingConsultationEdit &&
        (getAllocationComputationIsLocked({
          isBoExternal,
          allocationComputedBy: data.allocation_computed_by,
          choiceCollectedBy: data.choice_collected_by,
          campaignStatus,
          step: 'confirmation',
          distributorStepsOverride,
        }) ||
          (data.has_er_plan as boolean) ||
          getUnusedVacationDaysStepLocked(data, 'confirmation', campaignStatus))),
    hideDraft: (data: FieldValues) => isCampaignVacationDaysAllocation(data),
    saveDisabled: (data: FieldValues) => data.matching_contributions_total_is_zero === true,
    // While the consultation is ongoing there's no draft to save against (the campaign is
    // already published), so this falls through to the regular SaveButton/edit transform
    // instead of SaveDraftButton, which silently renders nothing without a `draft`.
    customSave: isOngoingConsultationEdit
      ? undefined
      : {
          transform: (data: FieldValues) => ({
            ...data,
            is_draft: false,
            allocation_computation_status:
              campaignStatus === 'campaign_awaiting_customer_confirmation'
                ? 'confirmed'
                : undefined,
            allocations: data.allocations.filter((entry: AllocationRecord) =>
              isAllocationAccepted({
                campaignType: data.type,
                amount: entry.amount ?? 0,
                companyCollectedChoices: entry.company_collected_choices,
              }),
            ),
          }),
          label: 'ra.action.save',
          onSuccess: (id) => {
            openConfirmationDialog({
              campaignId: id as number,
              title:
                action === 'edit'
                  ? 'resources.bo_common.b2b.campaigns.sections.success_modal_updated.title'
                  : 'resources.bo_common.b2b.campaigns.sections.success_modal.title',
              body:
                action === 'edit'
                  ? 'resources.bo_common.b2b.campaigns.sections.success_modal_updated.body'
                  : 'resources.bo_common.b2b.campaigns.sections.success_modal.body',
              showAllocations: true,
            });
          },
        },
  },
];
